import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { Globe, Briefcase, Star, Heart, Network, DollarSign, Building, CheckCircle, Users, Zap, Languages, Rocket } from "lucide-react"
import Layout from "@/components/Layout"
import SectionHeader from "@/components/SectionHeader"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { ROUTE_PATHS } from "@/lib/index"
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion"
import { IMAGES } from "@/assets/images"

const partnerTypes = [
  { icon: Globe, title: "Country Partners", description: "Launch Sammo as a localised platform in your country with full language and regulatory adaptation." },
  { icon: Briefcase, title: "Industry Solution Partners", description: "Deploy sector-specific configurations for insurance, banking, telco, retail and more." },
  { icon: Star, title: "Rewards Platforms", description: "Integrate Sammo as a high-value benefit within existing loyalty and rewards ecosystems." },
  { icon: Heart, title: "EAP Providers", description: "White-label Sammo household and wellness support within employee assistance programmes." },
  { icon: Network, title: "Distribution Networks", description: "Leverage existing client networks to distribute Sammo subscriptions at scale." },
]

const revenueStreams = [
  { icon: DollarSign, title: "Subscription Revenue Share", description: "Earn a share of every active subscription deployed through your network." },
  { icon: Building, title: "Enterprise Licensing", description: "License Sammo for deployment across large organisations and institutions." },
  { icon: CheckCircle, title: "White-label Deployments", description: "Deploy Sammo under your brand, co-branded, or campaign-specific identities." },
  { icon: Star, title: "Sector Exclusivity", description: "Secure exclusive rights to deploy Sammo within your industry or geography." },
]

const whySammoStats = [
  { icon: Users, label: "22+ Assistants" },
  { icon: Heart, label: "5 Family Members" },
  { icon: Languages, label: "Multilingual Delivery" },
  { icon: Globe, label: "Localisation Ready" },
  { icon: Rocket, label: "Fast to Market" },
  { icon: Zap, label: "White Label Ready" },
]

export default function Partners() {
  return (
    <Layout>
      <div className="min-h-screen">

        {/* HERO */}
        <section className="relative py-32 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${IMAGES.BUSINESS_TEAM_5})` }}
          />
          <div className="absolute inset-0 bg-primary/88" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="max-w-3xl mx-auto text-center"
            >
              <motion.span variants={staggerItem} className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-accent text-sm font-semibold mb-6">
                Partner with Sammo
              </motion.span>
              <motion.h1 variants={staggerItem} className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Grow your market with Sammo
              </motion.h1>
              <motion.p variants={staggerItem} className="text-xl text-white/80 mb-10 leading-relaxed">
                Launch proven family support solutions in your country, sector or customer network through white-label, co-branded or localised deployments.
              </motion.p>
              <motion.div variants={staggerItem} className="flex flex-wrap gap-4 justify-center">
                <Button asChild size="lg" className="text-base px-8 bg-accent hover:bg-accent/90 text-primary">
                  <Link to={ROUTE_PATHS.CONTACT}>Explore Your Market Opportunity</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-base px-8 border-white/30 text-white hover:bg-white/10">
                  <Link to={ROUTE_PATHS.CONTACT}>Book a Call</Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* PARTNER TYPES */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Partner Models"
              headline="Five ways to partner with Sammo"
              subheadline="Whether you're entering a new market, enhancing an existing product or building a distribution channel — Sammo has a partnership model for you."
              align="center"
            />
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mt-12"
            >
              {partnerTypes.map((type, i) => {
                const Icon = type.icon
                return (
                  <motion.div key={i} variants={staggerItem}>
                    <Card className="p-8 h-full hover:shadow-lg transition-all group rounded-2xl">
                      <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Icon className="w-7 h-7 text-accent" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{type.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{type.description}</p>
                    </Card>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        {/* REVENUE MODEL */}
        <section className="py-24 bg-muted/20">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Revenue Model"
              headline="Multiple ways to earn"
              subheadline="Sammo partners benefit from a range of commercial models designed to reward growth and scale."
              align="center"
            />
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mt-12"
            >
              {revenueStreams.map((stream, i) => {
                const Icon = stream.icon
                return (
                  <motion.div key={i} variants={staggerItem}>
                    <Card className="p-6 h-full text-center hover:shadow-md transition-all rounded-2xl">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-primary flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-bold mb-2">{stream.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{stream.description}</p>
                    </Card>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        {/* STATS BAR */}
        <section className="py-16 bg-primary">
          <div className="container mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 max-w-5xl mx-auto"
            >
              {whySammoStats.map((stat, i) => {
                const Icon = stat.icon
                return (
                  <motion.div key={i} variants={staggerItem} className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-accent" />
                    </div>
                    <p className="text-white font-semibold text-sm">{stat.label}</p>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        {/* QUOTE */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <p className="text-3xl md:text-4xl font-bold leading-relaxed mb-6">
                "The organisations that will lead Africa's next decade are those that deliver real value into the household — every single day."
              </p>
              <p className="text-muted-foreground text-lg">Sammo gives you the platform to do exactly that.</p>
            </motion.div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 bg-muted/20">
          <div className="container mx-auto px-4">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-2xl mx-auto text-center"
            >
              <h2 className="text-4xl font-bold mb-4">Let's explore your market opportunity</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Tell us about your market and we'll show you how Sammo can help you grow within it.
              </p>
              <Button asChild size="lg" className="text-base px-10">
                <Link to={ROUTE_PATHS.CONTACT}>Start the Conversation</Link>
              </Button>
            </motion.div>
          </div>
        </section>

      </div>
    </Layout>
  )
}
